import {
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Autocomplete
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { API } from 'aws-amplify';

interface AssetIssueFormProps {
  onClickCreateOverride: (
    assetID: number,
    data: {
      name: string;
      note: string;
      cellId: number;
      fcu1Value: string;
      fcu2Value: string;
      fcu3Value: string;
    }
  ) => void;
  onClickDeleteOverride: (
    overrideID: number
  ) => void;
  assetId: number;
  parameters: Parameter[];
  cells: Cell[];
}

interface Parameter {
  name: string;
  fcu: number;
  value: number;
  cellName: string;
  createdBy: string;
  created: string;
}

interface Cell {
  id: number;
  cellName: string;
}

const ParameterOverrideForm = React.forwardRef(
  (props: AssetIssueFormProps, ref) => {
    const [openStateModal, setOpenStateModal] = useState(false);
    const handleOpenModal = () => {
      loadParameterOverrides();
      setOpenStateModal(true);
    };
    const handleCloseModal = () => {
      setCellId(null);
      setName('');
      setNote('');
      setFcu1Value(null);
      setFcu2Value(null);
      setFcu3Value(null);
      setOpenStateModal(false);
    };
    const [cellId, setCellId] = useState(null);
    const [note, setNote] = useState('');
    const [parameterOverride, setParameterOverride] = useState([]);
    const [name, setName] = useState('');
    const [fcu1Value, setFcu1Value] = useState(null);
    const [fcu2Value, setFcu2Value] = useState(null);
    const [fcu3Value, setFcu3Value] = useState(null);

    const updateAutocomplete = (event, values) => {
      setName(values);
    };

    function loadParameterOverrides() {
      API.get('bom', `/parameter/override/` + props.assetId, {})
        .then(p => {
          setParameterOverride(p);
        })
        .catch(e => {
          console.error(e);
        });
    }

    function onCreateButtonClicked() {
      props.onClickCreateOverride(props.assetId, {
        note,
        name,
        cellId,
        fcu1Value,
        fcu2Value,
        fcu3Value
      });
      handleCloseModal();
    }

    function onDeleteButtonClicked(id) {
      props.onClickDeleteOverride(id);
      handleCloseModal();
    }

    return (
      <>
        <Button
          onClick={handleOpenModal}
          color="secondary"
          size="small"
          variant="contained">
          Parameter Overrides
        </Button>
        <Dialog
          onClose={handleCloseModal}
          open={openStateModal}
          BackdropProps={{ style: { backgroundColor: 'transparent' } }}
          PaperProps={{
            style: {
              margin: '10px 0px',
              width: '100%',
              maxWidth: 900,
              height: '90%',
              maxHeight: 1100
            },
          }}
        >
          <DialogTitle>Parameter Overrides</DialogTitle>
          <div style={{ padding: 12 }}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="assets table">
                <TableHead>
                  <TableRow>
                    <TableCell><TableSortLabel>Created</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>Created By</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>Cell</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>Name</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>FCU1 Value</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>FCU2 Value</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>FCU3 Value</TableSortLabel></TableCell>
                    <TableCell><TableSortLabel>Note</TableSortLabel></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parameterOverride && parameterOverride.map(paramoverride => (
                    <TableRow
                      key={paramoverride.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">{paramoverride.created}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.createdBy}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.cellName}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.name}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.fcu1Value}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.fcu2Value}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.fcu3Value}</TableCell>
                      <TableCell component="th" scope="row">{paramoverride.note}</TableCell>
                      <TableCell component="th" scope="row">
                        <Button
                          onClick={() => onDeleteButtonClicked(paramoverride.id)}
                          color="secondary"
                          size="small"
                          variant="contained">
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <DialogTitle>Create New Override</DialogTitle>
            <FormControl fullWidth margin="normal">
              <InputLabel id="cell-select-label">Cell</InputLabel>
              <Select
                labelId="cell-select-label"
                value={cellId}
                label="Cell"
                onChange={e => setCellId(e.target.value)}
              >
                {props.cells?.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.cellName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                id="parameter"
                options={props.parameters?.map(param => param?.name) || []}
                onChange={updateAutocomplete}
                onInputChange={updateAutocomplete}
                clearOnBlur={false}
                value={name}
                style={{ width: 300 }}
                renderInput={params => (
                  <TextField {...params} label="Parameter" variant="outlined" />
                )}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                label="FCU1 Value"
                value={fcu1Value}
                onChange={e => setFcu1Value(e.target.value)}
                type="text"
                placeholder="0.0"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*[.,]?[0-9]*' }}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                label="FCU2 Value"
                value={fcu2Value}
                onChange={e => setFcu2Value(e.target.value)}
                type="text"
                placeholder="0.0"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*[.,]?[0-9]*' }}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                label="FCU3 Value"
                value={fcu3Value}
                onChange={e => setFcu3Value(e.target.value)}
                type="text"
                placeholder="0.0"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*[.,]?[0-9]*' }}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                style={{ marginTop: 6 }}
                label="Note"
                variant="standard"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </FormControl>
          </div>
          <div style={{ padding: 12 }}>
            <Button
              onClick={onCreateButtonClicked}
              color="secondary"
              size="small"
              variant="contained">
              Create Override
            </Button>
          </div>
        </Dialog>
      </>
    );
  }
);

export default ParameterOverrideForm;
